import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { setGanttFilter } from '@actions/groups-actions';
import { colors } from '@constants/colors';
import FilterSelect from '@shared/filter-select';
import { buildFilterMenuItem } from '@utils/filter-utils';

/**
 * Custom hook for dropdown filter state and handlers.
 * @param {object} options - Hook options.
 * @param {string} options.name - Filter name in the gantt filters state.
 * @param {Function} options.getItems - Function to generate filter items.
 * @param {boolean} options.multiple - Whether multiple selection is allowed.
 * @param {Function} options.isChecked - Function to check if an item is selected.
 * @returns {object} Filter state and handlers.
 */
export const useDropdownFilter = ({ name, getItems, multiple = false, isChecked }) => {
  const dispatch = useDispatch();
  const filter = useSelector(state => state.groups.gantt.filters[name]);
  const items = getItems();

  const [selected, setSelected] = useState(filter);

  const filterChange = useCallback(event => {
    setSelected(event.target.value);
  }, []);

  const filterClose = useCallback(() => {
    dispatch(setGanttFilter(name, selected));
  }, [dispatch, name, selected]);

  const getChecked = useCallback(item => {
    if (isChecked) {
      return isChecked(selected, item);
    }
    return selected === item.id;
  }, [isChecked, selected]);

  return {
    dispatch,
    filter,
    filterChange,
    filterClose,
    getChecked,
    items,
    multiple,
    selected
  };
};

/**
 * Base dropdown filter component.
 * @param {object} props - Component props.
 * @param {Array} props.items - Filter items.
 * @param {boolean} props.multiple - Whether multiple selection is allowed.
 * @param {Function} props.filterChange - Handler for filter change.
 * @param {Function} props.filterClose - Handler for filter close.
 * @param {Function} props.renderValue - Function to render selected value.
 * @param {Function} props.getChecked - Function to check if item is selected.
 * @param {*} props.selected - Currently selected value.
 * @returns {JSX.Element} The filter component.
 */
export const DropdownFilter = ({
  items,
  multiple,
  filterChange,
  filterClose,
  renderValue,
  getChecked,
  selected
}) => (
  <div>
    <FilterSelect
      displayEmpty
      isValueSelected={!R.isEmpty(selected)}
      labelStyles={{
        backgroundColor: 'rgba(255,255,255,0.3)',
        margin: '8px 0 8px 0',
        color: colors.neutral.white
      }}
      multiple={multiple}
      onChange={filterChange}
      onClose={filterClose}
      renderValue={renderValue}
      value={selected}
    >
      {items.map(item => buildFilterMenuItem(getChecked(item), null, item))}
    </FilterSelect>
  </div>
);

DropdownFilter.propTypes = {
  filterChange: PropTypes.func.isRequired,
  filterClose: PropTypes.func.isRequired,
  getChecked: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  multiple: PropTypes.bool,
  renderValue: PropTypes.func.isRequired,
  selected: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number])
};
